import React from 'react';

import { di } from 'react-magnetic-di';

import type { DocNode } from '@atlaskit/adf-schema';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { ChatEntryPoint } from '@atlaskit/rovo-triggers/chat-entry-point';
import { Subscriber } from '@atlaskit/rovo-triggers/main';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { useAgentsPaginatedStoreActions } from '@atlassian/conversation-assistant-agent/controllers/agents';
import type { RovoApp } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store/types';
import { useFileAttachmentsStore } from '@atlassian/conversation-assistant-chat-prompt-input/useFileAttachmentsStore';
import { isTextFile } from '@atlassian/conversation-assistant-chat-prompt-input/utils/fileTypeHelpers';
import {
	encodeAdf,
	encodeSkillPromptToAdf,
} from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import {
	type FollowUpSource,
	type InteractionSource,
	SMART_LINK_ROVO_BUTTON_INTERACTION_SOURCE,
} from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { addSentryBreadcrumb } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useChatContextStoreActions } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import { useChatSignals } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import {
	type EmptyChatLaunchProjectContext,
	useUIStoreActions,
	useUIStoreSubscription,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { useEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useEditorPlaceholder';
import { useFireInsightCardClicked } from '@atlassian/rovo-growth-pulse/common/analytics/useFireInsightCardClicked';
import {
	resolveInsightIcon,
	resolveInsightIconAppearance,
} from '@atlassian/rovo-growth-pulse/ui/insights-feed/resolveInsightIcon';

import { fileCache } from '../../common/utils/file-cache';
import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { usePaginatedAgentByConfigReferenceWrapped } from '../../controllers/agents';
import { useAgentsModalActions } from '../../controllers/agents-modal/useAgentsModalActions';
import { type MessageSource } from '../../controllers/chat-store/types';
import { useChatStore } from '../../controllers/chat-store/useChatStore';
import { useDebugController } from '../../controllers/debug';
import { useFeedbackCollector } from '../../controllers/feedback-collector/useFeedbackCollector';
import { useForgeAuthController } from '../../controllers/forge-auth/useForgeAuthController';
import { useSeedInsightConversation } from '../../controllers/seed-insight-conversation/useSeedInsightConversation';

const getAgentFromParams = async ({
	agentId,
	agentExternalConfigReference,
	getAndStoreAgentByExternalConfigReference,
}: {
	agentId?: string;
	agentExternalConfigReference?: string;
	getAndStoreAgentByExternalConfigReference: ReturnType<
		typeof usePaginatedAgentByConfigReferenceWrapped
	>['getAndStoreAgentByExternalConfigReference'];
}): Promise<{ agentId: string | undefined; agentIdentityAccountId: string | undefined }> => {
	// Short-circuit: agentId already known, no network call needed
	if (agentId) {
		return { agentId, agentIdentityAccountId: undefined };
	}

	if (agentExternalConfigReference) {
		const agent = await getAndStoreAgentByExternalConfigReference({
			externalConfigReference: agentExternalConfigReference,
		});
		return {
			agentId: agent?.id,
			agentIdentityAccountId: agent?.identity_account_id ?? undefined,
		};
	}

	return { agentId: undefined, agentIdentityAccountId: undefined };
};

/**
 * Lightweight runtime guard for chat-new prompts carrying ADF.
 * This only confirms the value looks like a doc node; it is not full ADF schema validation.
 */
const EMPTY_CHAT_INPUT_ADF: DocNode = {
	type: 'doc',
	version: 1,
	content: [{ type: 'paragraph' }],
};

const isAdfDocPrompt = (prompt: unknown): prompt is DocNode => {
	if (typeof prompt !== 'object' || prompt === null) {
		return false;
	}

	const maybeDoc = prompt as { content?: unknown; type?: unknown };

	return maybeDoc.type === 'doc' && Array.isArray(maybeDoc.content);
};

const isEditorAgentMentionsExperimentEnabled = (): boolean =>
	expValEquals('platform_editor_agent_mentions', 'isEnabled', true);

const isChatEntryPoint = (value: unknown): value is ChatEntryPoint =>
	Object.values(ChatEntryPoint).includes(value as ChatEntryPoint);

export const PubSubListener = (): React.JSX.Element => {
	di(
		fg,
		Subscriber,
		useChatSignals,
		useChatStore,
		useDebugController,
		useFeedbackCollector,
		useEditorPlaceholder,
	);

	const [
		{ conversations, currentConversationId, checkActiveConversationMatchesSource },
		{
			sendMessage,
			createSeededConversation,
			setSelectedConversation,
			handleChatOpenPubsubAction,
			createConversationOptimistically,
		},
	] = useChatStore();

	const {
		preferences: { storeUserMessages },
	} = useUserPreferences();

	const { openAgentModal } = useAgentsModalActions();
	const { setPlaceholderAgent } = useAgentsPaginatedStoreActions();
	const { addForgeFailureMessage, handle3pActionAuth } = useForgeAuthController();
	const experienceTracker = useAIMateExperienceTracker();
	const { clearPlaceholder, isPromptWithPlaceholder, insertPlaceholder, insertPlaceholderWithAdf } =
		useEditorPlaceholder();
	const [, { addFiles: addFilesToAttachmentStore }] =
		useFileAttachmentsStore(EMPTY_CONVERSATION_ID);
	const [{ status }] = useChatSignals();
	const { closeChat } = useGlobalConfig();
	const { setAIFeatureContext } = useChatContextStoreActions();
	useUIStoreSubscription();
	const {
		setActiveMenu,
		setIsChatOnboardingMiniModalOpen,
		setIsAgentSelectorMenuOpen,
		setEmptyChatLaunch,
		setSelectedSpace,
		setSmartLink3PPostAuth,
		clearSmartLink3PPostAuth,
	} = useUIStoreActions();
	const { getAndStoreAgentByExternalConfigReference } = usePaginatedAgentByConfigReferenceWrapped({
		touchpointSource: 'pubsub-listener',
	});
	const { openDebugModal } = useDebugController();
	const [, { open: openFeedbackModal }] = useFeedbackCollector();
	const seedInsightConversation = useSeedInsightConversation();
	const { fireInsightCardClicked } = useFireInsightCardClicked();

	/**
	 * Conditional subscriptions
	 */
	return (
		<>
			{status === 'online' && (
				<Subscriber
					topic="ai-mate"
					triggerLatest
					flushQueueOnUnmount
					consumeOnceKey="conversation-assistant-pubsub-listener"
					onEvent={async (payload) => {
						addSentryBreadcrumb(`PubSubListener onEvent: ${payload.type}`);
						experienceTracker.start({
							id: ExperienceName.PUBSUB_LISTENER,
							name: ExperienceName.PUBSUB_LISTENER,
							attributes: { taskId: payload.type },
						});

						try {
							if (
								payload.type === 'insights-open-in-chat' &&
								FeatureGates.getExperimentValue('rovo_growth_chat_pulse', 'isEnabled', false)
							) {
								// Icons arrive as raw string keys (React components can't cross the
								// event bus) and are re-resolved here; the back-button label is
								// supplied panel-side by the seeding hook.
								const { adf, conversationTitle, iconKey, iconColor, insightCategory, followUps } =
									payload.data;
								// Mirror the in-panel feed's click analytics so external surfaces
								// (e.g. the home insights carousel) aren't behaviorally divergent.
								// Fired before seeding, matching the feed's onInsightCardClicked order.
								fireInsightCardClicked(insightCategory);
								await seedInsightConversation(adf, {
									conversationTitle,
									conversationIcon: resolveInsightIcon(iconKey),
									conversationIconAppearance: resolveInsightIconAppearance(iconColor),
									followUps: followUps ?? [],
								});
							}

							if (
								payload.type === 'chat-smartlink-3p-post-auth-launch' &&
								fg('platform_sl_3p_post_auth_chat_open_fg')
							) {
								setIsChatOnboardingMiniModalOpen(false);
								setActiveMenu('none');
								setIsAgentSelectorMenuOpen(false);
								setSmartLink3PPostAuth({
									isActive: true,
									extensionKey: payload.data.extensionKey,
									provider: payload.data.provider,
									projectContext: payload.data.projectContext,
								});
								if (payload.openChat) {
									handleChatOpenPubsubAction({ conversationId: EMPTY_CONVERSATION_ID });
								}
							}

							if (payload.type === 'message-send') {
								const { prompt, productKey, minionAlias, files } = payload.data;

								const messageSource: MessageSource = payload.interactionSource
									? {
											interactionSource: payload.interactionSource as InteractionSource,
											followUpSource: payload.source as FollowUpSource,
										}
									: {
											interactionSource: 'movedOver',
											followUpSource: payload.source as FollowUpSource,
										};

								await sendMessage({
									prompt: encodeAdf(prompt),
									// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
									// value when BE user settings are fully rolled out; BE overrides it via the capability header.
									storeUserMessages,
									messageSources: messageSource,
									productKey,
									minionAlias,
									files,
								});
							}

							if (payload.type === 'chat-close') {
								if (fg('platform_sl_3p_post_auth_chat_open_fg')) {
									clearSmartLink3PPostAuth();
								}
								closeChat();
							}

							if (payload.type === 'chat-new') {
								if (fg('platform_sl_3p_post_auth_chat_open_fg')) {
									clearSmartLink3PPostAuth();
								}
								// Set project context from payload if provided, otherwise clear stale context.
								const payloadAIFeatureContext = payload.data.aiFeatureContext;
								setAIFeatureContext({
									contextKey: 'projectContext',
									setContext: <T,>(_: T): T => (payloadAIFeatureContext?.projectContext ?? {}) as T,
								});
								const smartLinkRovoInvokedFrom =
									payload.interactionSource === SMART_LINK_ROVO_BUTTON_INTERACTION_SOURCE
										? payload.interactionSource
										: undefined;

								// When a project context is present (e.g. Daily Brief), set it in the
								// UI store so the context panel shows the project URL instead of the Confluence page.
								// This mirrors what handleChatEmptyLaunch does for the Chat button path.
								if (payloadAIFeatureContext?.projectContext) {
									setEmptyChatLaunch({
										projectContext: payloadAIFeatureContext?.projectContext as
											| EmptyChatLaunchProjectContext
											| undefined,
										...(smartLinkRovoInvokedFrom ? { invokedFrom: smartLinkRovoInvokedFrom } : {}),
									});
								}
								// Smart Link toolbar launches don't carry project context, but still need
								// one-shot attribution for the first result viewed event.
								else if (smartLinkRovoInvokedFrom) {
									setEmptyChatLaunch({ invokedFrom: smartLinkRovoInvokedFrom });
								}

								// When a selectedSpace is present, set the SpaceBar in the chat input.
								// This mirrors what handleChatEmptyLaunch does for the Chat button path.
								if (payload.data.selectedSpace) {
									setSelectedSpace(payload.data.selectedSpace);
								}

								const {
									name,
									dialogues = [],
									prompt,
									sourceId,
									contentContext,
									files,
									isPromptPlaceholder,
									overrideAutoSend,
									skipCreatingSeededConversation,
									resetActiveMenu,
									agentVersion,
									resultAttributionContext,
									searchArtifact,
								} = payload.data;
								const mode = (
									payload.data as {
										mode?: {
											deepResearchEnabled?: boolean;
											thinkDeeperEnabled?: boolean;
											fastModeEnabled?: boolean;
											taskModeEnabled?: boolean;
											webSearchEnabled?: boolean;
											useCurrentPageContext?: boolean;
											appFilters?: RovoApp[];
										};
									}
								).mode;
								const isEditorAgentMentionsEnabled = isEditorAgentMentionsExperimentEnabled();

								if (isEditorAgentMentionsEnabled && prompt) {
									// Clear the visible input before seeding so stale text is not shown while the
									// seeded conversation backend call is pending. The drop1 fixes path avoids
									// replaceDocument, which can blur the editor while the mini-modal opens.
									if (fg('platform_editor_agent_mentions_drop_one_fixes')) {
										insertPlaceholderWithAdf('', EMPTY_CHAT_INPUT_ADF, { shouldFocus: false });
									} else {
										insertPlaceholderWithAdf('', EMPTY_CHAT_INPUT_ADF);
									}
								}

								const {
									agentId: agentIdFromPayload,
									agentExternalConfigReference,
									agentIdentityAccountId: agentIdentityAccountIdFromPayload,
								} = payload.data;

								const { agentId, agentIdentityAccountId: resolvedIdentityAccountId } =
									await getAgentFromParams({
										agentId: agentIdFromPayload,
										agentExternalConfigReference,
										getAndStoreAgentByExternalConfigReference,
									});

								const conversationResultAttributionContext = resultAttributionContext?.launchSource
									? {
											launchSource: resultAttributionContext.launchSource as FollowUpSource,
											...(agentId ? { sourceAgentId: agentId } : {}),
										}
									: undefined;

								// Stored on the conversation so attribution also covers launches with no
								// `prompt` and continues past the first turn.
								const requestedChatEntryPoint = (payload.data as { chatEntryPoint?: unknown })
									.chatEntryPoint;
								const conversationChatEntryPoint = isChatEntryPoint(requestedChatEntryPoint)
									? requestedChatEntryPoint
									: undefined;

								if (resetActiveMenu) {
									setActiveMenu('none');
								}

								// When the chat-new originates from a Rovo Space surface (the
								// SpaceLandingPage chat input or a space starter card), capture the
								// spaceId so the optimistic / seeded conversation can be scoped to
								// the space immediately. Without this the new conversation briefly
								// appears in the regular conversation list before the backend
								// response catches up.
								const isSpaceLandingNewChatSource =
									payload.source === 'space-landing-chat-input' ||
									payload.source === 'space-landing-starter';
								const newChatSpaceId = (payload.data as { spaceId?: string }).spaceId;
								const rovoSpaceIdForNewChat =
									isSpaceLandingNewChatSource && newChatSpaceId ? newChatSpaceId : undefined;

								// Skip conversation creation if the current active conversation
								// matches the sourceId. This indicates the conversation was created
								// by a similar prior action and already contextually present to the user
								if (!checkActiveConversationMatchesSource(sourceId)) {
									const conversationTags = (payload.data as { tags?: string[] }).tags;
									if (skipCreatingSeededConversation) {
										createConversationOptimistically({
											sourceAgentId: agentId,
											chatEntryPoint: conversationChatEntryPoint,
											...(conversationResultAttributionContext
												? { resultAttributionContext: conversationResultAttributionContext }
												: {}),
											...(agentVersion ? { agentVersion } : {}),
											...(rovoSpaceIdForNewChat ? { rovoSpaceId: rovoSpaceIdForNewChat } : {}),
										});
									} else {
										await createSeededConversation({
											name,
											dialogues,
											sourceId,
											followUpSource: payload.source as FollowUpSource,
											agentId,
											chatEntryPoint: conversationChatEntryPoint,
											...(conversationResultAttributionContext
												? { resultAttributionContext: conversationResultAttributionContext }
												: {}),
											...(agentVersion ? { agentVersion } : {}),
											...(rovoSpaceIdForNewChat ? { rovoSpaceId: rovoSpaceIdForNewChat } : {}),
											...(conversationTags ? { tags: conversationTags } : {}),
											...(mode && {
												mode: {
													deepResearchEnabled: mode.deepResearchEnabled,
													thinkDeeperEnabled: mode.thinkDeeperEnabled,
													fastModeEnabled: mode.fastModeEnabled,
													taskModeEnabled: mode.taskModeEnabled,
													webSearchEnabled: mode.webSearchEnabled,
													useCurrentPageContext: mode.useCurrentPageContext,
													appFilters: mode.appFilters as RovoApp[] | undefined,
												},
											}),
										});
									}
								}

								if (payload.source === 'ai-mate-onboarding') {
									setIsChatOnboardingMiniModalOpen(false);
								}

								// skip the chat onboarding for aifc onboarding flow
								// https://switcheroo.atlassian.com/ui/gates/8d540664-7539-4425-9b90-922bcfaaf054/key/cfind-5139-confluence-aifc-onboarding
								// Also skip for the CWR nth-user onboarding flow so the mini-modal is
								// dismissed when a CWR-seeded chat arrives.
								if (
									sourceId === 'aifc-onboarding-confluence' ||
									sourceId === 'cwr-nth-user-onboarding-confluence'
								) {
									setIsChatOnboardingMiniModalOpen(false);
								}

								if (payload.source === 'smartLink-3p-post-auth-chip') {
									clearSmartLink3PPostAuth();
								}

								// Follow up with a prompt after creating a seeded conversation
								if (prompt) {
									const isAdfPromptContent = isAdfDocPrompt(prompt);
									const shouldInsertStringPrompt =
										typeof prompt === 'string' &&
										// Insert the prompt as an editable draft (instead of sending it) when it is a
										// placeholder template, or when the publisher explicitly opts into drafting via
										// `overrideAutoSend`.
										((!!isPromptPlaceholder && isPromptWithPlaceholder(prompt)) ||
											overrideAutoSend);

									const isDraftInsert =
										(isAdfPromptContent && overrideAutoSend) || shouldInsertStringPrompt;

									if (isAdfPromptContent && overrideAutoSend) {
										// The ADF document is the rendered input content.
										insertPlaceholderWithAdf('', prompt);
									} else if (shouldInsertStringPrompt) {
										insertPlaceholder(prompt, payload.data.placeholderType);
									}

									if (
										isDraftInsert &&
										payload.interactionSource &&
										fg('add_interactionsource_to_draft_chat_analytics')
									) {
										setEmptyChatLaunch({
											interactionSource: payload.interactionSource as InteractionSource,
											...(smartLinkRovoInvokedFrom
												? { invokedFrom: smartLinkRovoInvokedFrom }
												: {}),
										});
									}

									if (!isDraftInsert) {
										// Populate file cache for thumbnail previews (same as chat-input-refresh onSubmit)
										if (files?.length) {
											files.forEach((file: { id?: string; fileObject?: File }) => {
												if (
													file.id &&
													file.fileObject &&
													(file.fileObject.type.startsWith('image/') ||
														file.fileObject.type === 'application/pdf' ||
														isTextFile(file.fileObject.type, file.fileObject.name))
												) {
													fileCache.set(file.id, file.fileObject);
												}
											});
										}
										const spaceId = payload.data.spaceId as string | undefined;

										const isSkillPrompt = payload.data.placeholderType === 'skill';
										const isSmartLink3PPostAuthChip =
											payload.source === 'smartLink-3p-post-auth-chip';
										await sendMessage({
											prompt:
												typeof prompt === 'string' && !!isPromptPlaceholder && isSkillPrompt
													? encodeSkillPromptToAdf(prompt).adf
													: encodeAdf(prompt),
											// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
											// value when BE user settings are fully rolled out; BE overrides it via the capability header.
											storeUserMessages,
											messageSources: {
												interactionSource: (payload.interactionSource ??
													'followUps') as InteractionSource,
												followUpSource: payload.source as Exclude<FollowUpSource, 'none'>,
											},
											agentId,
											...((agentIdentityAccountIdFromPayload ?? resolvedIdentityAccountId)
												? {
														agentIdentityAccountId:
															agentIdentityAccountIdFromPayload ??
															resolvedIdentityAccountId ??
															undefined,
													}
												: {}),
											source: payload.source,
											contentContext: contentContext,
											minionAlias: payload.data.minionAlias,
											files,
											skillSelectionSource: isSkillPrompt ? 'rovoChatAction' : undefined,
											...(searchArtifact ? { searchArtifact } : {}),
											...(spaceId ? { spaceId } : {}),
											...(mode
												? {
														deepResearchEnabled: mode.deepResearchEnabled,
														thinkDeeperEnabled: mode.thinkDeeperEnabled,
														fastModeEnabled: mode.fastModeEnabled,
														taskModeEnabled: mode.taskModeEnabled,
														webSearchEnabled: mode.webSearchEnabled,
														appFilters: mode.appFilters as RovoApp[] | undefined,
													}
												: {}),
											addCurrentPageAsContextUserPreference: isSmartLink3PPostAuthChip
												? false
												: mode?.useCurrentPageContext,
										});
									}
								}
							}

							if (payload.type === 'chat-draft') {
								setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID });
							}

							if (payload.type === 'open-browse-agent-modal') {
								openAgentModal();
							}

							if (payload.type === 'open-browse-agent-sidebar') {
								setActiveMenu('agents');
							}

							if (payload.type === 'chat-open') {
								if (fg('platform_sl_3p_post_auth_chat_open_fg')) {
									clearSmartLink3PPostAuth();
								}

								const {
									resetActiveMenu,
									agentId,
									agentName,
									agentIdentityAccountId,
									avatarUrl,
									openAgentSelector,
								} = payload.data;

								if (resetActiveMenu) {
									setActiveMenu('none');
								}

								// Set placeholder agent in the agent store for immediate header rendering
								// with name, avatar, and identity data from the Jira session
								// The full agent details will be fetched by useAgentsPaginatedByAgentId
								if (agentId && agentName) {
									setPlaceholderAgent(agentId, {
										name: agentName,
										...(agentIdentityAccountId && avatarUrl
											? {
													identity_account_id: agentIdentityAccountId,
													placeholderAvatarUrl: avatarUrl,
												}
											: {}),
									});
								}

								handleChatOpenPubsubAction({
									conversationId: payload.data.channelId,
									...(agentId ? { agentId } : {}),
								});

								if (openAgentSelector) {
									setIsAgentSelectorMenuOpen(true);
								}
							}

							if (payload.type === 'forge-auth-success') {
								handle3pActionAuth(payload.data?.agentMessageId, payload.data?.authUrl);
							}

							if (payload.type === 'forge-auth-failure') {
								addForgeFailureMessage(payload.data.errorMessage);
							}

							if (payload.type === 'insert-prompt') {
								clearPlaceholder();

								const {
									channelId,
									agentId: overrideAgentId,
									agentName,
									agentIdentityAccountId,
									avatarUrl,
								} = payload.data;

								if (overrideAgentId && agentName && fg('ai_agent_sessions_refinement')) {
									setPlaceholderAgent(overrideAgentId, {
										name: agentName,
										...(agentIdentityAccountId && avatarUrl
											? {
													identity_account_id: agentIdentityAccountId,
													placeholderAvatarUrl: avatarUrl,
												}
											: {}),
									});
								}

								if (overrideAgentId && channelId && fg('ai_agent_sessions_refinement')) {
									setSelectedConversation({
										conversationId: channelId,
										sourceAgentId: overrideAgentId,
									});
								}

								if (
									isPromptWithPlaceholder(payload.data.prompt) ||
									(payload.data.overrideAutoSend &&
										fg('fix_dynamic_prompts_in_rovo_action_trigger'))
								) {
									const { prompt, placeholderType, promptAdf } = payload.data;
									if (promptAdf && fg('rovo_insert_prompt_adf')) {
										insertPlaceholderWithAdf(prompt, promptAdf);
									} else {
										insertPlaceholder(prompt, placeholderType);
									}
									if (payload.data.files?.length) {
										addFilesToAttachmentStore(
											EMPTY_CONVERSATION_ID,
											payload.data.files.map((f) => ({
												...f,
												isLoading: false,
											})),
											() => {},
										);
									}
								} else {
									const { prompt, placeholderType } = payload.data;

									const currentAgentId = conversations[currentConversationId]?.sourceAgentId;
									const agentId =
										overrideAgentId && fg('ai_agent_sessions_refinement')
											? overrideAgentId
											: currentAgentId;
									const isSkillPlaceholder = placeholderType === 'skill';
									await sendMessage({
										prompt:
											typeof prompt === 'string' && isSkillPlaceholder
												? encodeSkillPromptToAdf(prompt).adf
												: encodeAdf(prompt),
										// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
										// value when BE user settings are fully rolled out; BE overrides it via the capability header.
										storeUserMessages,
										messageSources: {
											interactionSource: (payload.interactionSource ??
												'followUps') as InteractionSource,
											followUpSource: payload.source as Exclude<FollowUpSource, 'none'>,
										},
										agentId,
										...(channelId ? { conversationId: channelId } : {}),
										skillSelectionSource: isSkillPlaceholder ? 'rovoChatAction' : undefined,
									});
								}
							}

							if (payload.type === 'open-chat-debug-modal') {
								openDebugModal();
							}

							if (payload.type === 'open-chat-feedback-modal') {
								openFeedbackModal({
									answerQuality: payload.data.answerQuality,
								});
							}
						} catch (error) {
							experienceTracker.fail({
								name: ExperienceName.PUBSUB_LISTENER,
								error: error as Error,
								attributes: {
									taskId: payload.type,
									payloadSource: payload.source,
								},
							});
						}

						experienceTracker.succeed({
							name: ExperienceName.PUBSUB_LISTENER,
							attributes: { taskId: payload.type },
						});
					}}
				/>
			)}
		</>
	);
};
